/**
 * Presentation helpers for personal Analytics.
 *
 * These keep chart colouring, empty-series detection and period trimming out of
 * the page component so the same rules can be tested without mounting Chart.js.
 */

export const ANALYTICS_CHART = {
  gold: '#C6A75E',
  goldFill: 'rgba(198, 167, 94, 0.28)',
  silk: 'rgba(230, 230, 232, 0.82)',
  silkMute: 'rgba(230, 230, 232, 0.14)',
  silkFill: 'rgba(230, 230, 232, 0.07)',
  grid: 'rgba(230, 230, 232, 0.055)',
  tick: 'rgba(230, 230, 232, 0.42)',
  tooltipBg: 'rgba(18, 18, 22, 0.96)',
  tooltipBorder: 'rgba(230, 230, 232, 0.12)',
  tooltipTitle: '#E6E6E8',
  tooltipBody: 'rgba(230, 230, 232, 0.78)',
};

const WEEKDAY_SHORT = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
};

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function trimFutureMonths(rows, now = new Date()) {
  if (!Array.isArray(rows)) return [];
  const cap = now.getMonth() + 1;
  return rows.filter((row) => {
    if (row == null || row.month == null) return true;
    const month = Number(row.month);
    return Number.isFinite(month) ? month <= cap : true;
  });
}

export function lastN(rows, n) {
  if (!Array.isArray(rows)) return [];
  if (!n || n >= rows.length) return rows;
  return rows.slice(rows.length - n);
}

export function trimLeadingEmpty(rows, keys = ['count']) {
  if (!Array.isArray(rows) || rows.length <= 3) return rows || [];
  const first = rows.findIndex((row) => keys.some((key) => Number(row?.[key]) > 0));
  if (first <= 0) return rows;
  return rows.slice(Math.max(0, first - 1));
}

export function seriesHasValues(rows, keys = ['count']) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row) => keys.some((key) => Number(row?.[key]) > 0));
}

export function peakIndex(values) {
  if (!Array.isArray(values) || values.length === 0) return -1;
  let max = -Infinity;
  let index = -1;
  values.forEach((value, i) => {
    const n = Number(value) || 0;
    if (n > max) {
      max = n;
      index = i;
    }
  });
  return max > 0 ? index : -1;
}

export function barFillColors(values) {
  const peak = peakIndex(values);
  return values.map((_, i) => (i === peak ? ANALYTICS_CHART.goldFill : ANALYTICS_CHART.silkMute));
}

export function barBorderColors(values) {
  const peak = peakIndex(values);
  return values.map((_, i) => (i === peak ? ANALYTICS_CHART.gold : 'rgba(230, 230, 232, 0.2)'));
}

export function shortWeekday(label) {
  return WEEKDAY_SHORT[label] || label;
}

export function monthLabel(row, { compact = false } = {}) {
  if (!row) return '';
  if (compact) return row.label || '';
  const year = row.year != null ? String(row.year).slice(-2) : '';
  return year ? `${row.label} ${year}` : (row.label || '');
}

export function rankedShares(items, { labelKey = 'label', valueKey = 'count' } = {}) {
  const list = Array.isArray(items) ? items : [];
  const cleaned = list.filter((item) => {
    const label = item?.[labelKey];
    const value = Number(item?.[valueKey]) || 0;
    return value > 0 && label && label !== 'No data yet';
  });
  const total = cleaned.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
  return cleaned
    .map((item) => {
      const value = Number(item[valueKey]) || 0;
      return {
        label: item[labelKey],
        value,
        percent: total > 0 ? Math.round((value / total) * 100) : 0,
      };
    })
    .sort((a, b) => b.value - a.value);
}

export function formatSignedPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  if (n > 0) return `+${n}%`;
  return `${n}%`;
}

export function growthTone(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 'neutral';
  return n > 0 ? 'up' : 'down';
}

export function contactsSectionTitle(source) {
  return source === 'profile_views' ? 'Top contacts' : 'Recently added';
}

export function buildAnalyticsChartOptions({
  reducedMotion = false,
  integerY = true,
  suggestedMax,
  maxTicksLimit = 8,
} = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: reducedMotion ? false : { duration: 420, easing: 'easeOutQuart' },
    layout: { padding: { top: 4, right: 4, bottom: 0, left: 0 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: ANALYTICS_CHART.tooltipBg,
        titleColor: ANALYTICS_CHART.tooltipTitle,
        bodyColor: ANALYTICS_CHART.tooltipBody,
        borderColor: ANALYTICS_CHART.tooltipBorder,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        titleFont: { size: 12, weight: '600', family: 'Inter, system-ui, sans-serif' },
        bodyFont: { size: 12, family: 'Inter, system-ui, sans-serif' },
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: ANALYTICS_CHART.tick,
          font: { size: 11, family: 'Inter, system-ui, sans-serif' },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit,
          padding: 8,
        },
      },
      y: {
        border: { display: false },
        beginAtZero: true,
        suggestedMax,
        grid: {
          color: ANALYTICS_CHART.grid,
          drawTicks: false,
        },
        ticks: {
          color: ANALYTICS_CHART.tick,
          font: { size: 11, family: 'Inter, system-ui, sans-serif' },
          precision: integerY ? 0 : undefined,
          padding: 8,
          maxTicksLimit: 5,
        },
      },
    },
  };
}

export function barDataset(label, values) {
  return {
    label,
    data: values,
    backgroundColor: barFillColors(values),
    borderColor: barBorderColors(values),
    borderWidth: 1,
    borderRadius: 7,
    borderSkipped: false,
    maxBarThickness: 36,
    categoryPercentage: 0.72,
    barPercentage: 0.86,
  };
}

export function lineDataset(label, values, { accent = false } = {}) {
  return {
    label,
    data: values,
    borderColor: accent ? ANALYTICS_CHART.gold : ANALYTICS_CHART.silk,
    backgroundColor: accent ? ANALYTICS_CHART.goldFill : ANALYTICS_CHART.silkFill,
    fill: true,
    tension: 0.35,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 4,
    pointHitRadius: 12,
    pointBackgroundColor: accent ? ANALYTICS_CHART.gold : ANALYTICS_CHART.silk,
    pointBorderWidth: 0,
  };
}
